import { supabase } from '../lib/supabase';

/**
 * Activity Feed Service
 * Aggregates platform activity data from songs, user_profiles, user_lesson_progress, and user_xp_events
 */

/**
 * Fetch recent platform activities for admin oversight
 * @param {Object} options - Filter options
 * @param {string} options.actionType - Filter by action type ('all', 'song_addition', 'profile_update', 'course_completion')
 * @param {string} options.userType - Filter by user type ('all', 'superuser', 'editor', 'user')
 * @param {number} options.timeRange - Time range in hours (24, 168, 720)
 * @param {number} options.limit - Number of activities to fetch
 * @returns {Promise<Array>} Array of activity objects
 */
export const fetchRecentActivities = async ({
  actionType = 'all',
  userType = 'all',
  timeRange = 168, // 7 days default
  limit = 50
} = {}) => {
  try {
    const cutoffTime = new Date();
    cutoffTime?.setHours(cutoffTime?.getHours() - timeRange);
    const cutoffTimestamp = cutoffTime?.toISOString();

    const activities = [];

    // Fetch song additions (if actionType allows)
    if (actionType === 'all' || actionType === 'song_addition') {
      const { data: songAdditions, error: songError } = await supabase?.from('songs')?.select(`
          id,
          title,
          created_at,
          created_by,
          musical_id,
          musicals (
            title,
            poster_url
          ),
          user_profiles!songs_created_by_fkey (
            id,
            full_name,
            username,
            profile_photo_url,
            role
          )
        `)?.gte('created_at', cutoffTimestamp)?.not('created_by', 'is', null)?.order('created_at', { ascending: false })?.limit(limit);

      if (songError) throw songError;

      songAdditions?.forEach(song => {
        if (!song?.user_profiles) return;
        
        // Apply user type filter
        if (userType !== 'all' && song?.user_profiles?.role !== userType) return;

        activities?.push({
          id: `song_${song?.id}`,
          type: 'song_addition',
          action: 'Song Added',
          description: `added "${song?.title}" to ${song?.musicals?.title || 'a musical'}`,
          user: {
            id: song?.user_profiles?.id,
            name: song?.user_profiles?.full_name || song?.user_profiles?.username,
            username: song?.user_profiles?.username,
            avatar: song?.user_profiles?.profile_photo_url,
            role: song?.user_profiles?.role
          },
          metadata: {
            songTitle: song?.title,
            songId: song?.id,
            musicalTitle: song?.musicals?.title,
            musicalId: song?.musical_id,
            posterUrl: song?.musicals?.poster_url
          },
          timestamp: song?.created_at,
          navigateTo: `/song-detail-page?id=${song?.id}`
        });
      });
    }

    // Fetch profile updates (if actionType allows)
    if (actionType === 'all' || actionType === 'profile_update') {
      const { data: profileUpdates, error: profileError } = await supabase?.from('user_profiles')?.select('id, full_name, username, profile_photo_url, role, updated_at, email')?.gte('updated_at', cutoffTimestamp)?.neq('created_at', supabase?.raw('updated_at'))?.order('updated_at', { ascending: false })?.limit(limit);

      if (profileError) throw profileError;

      profileUpdates?.forEach(profile => {
        // Apply user type filter
        if (userType !== 'all' && profile?.role !== userType) return;

        activities?.push({
          id: `profile_${profile?.id}_${profile?.updated_at}`,
          type: 'profile_update',
          action: 'Profile Updated',
          description: `updated their profile information`,
          user: {
            id: profile?.id,
            name: profile?.full_name || profile?.username,
            username: profile?.username,
            avatar: profile?.profile_photo_url,
            role: profile?.role
          },
          metadata: {
            profileId: profile?.id,
            email: profile?.email
          },
          timestamp: profile?.updated_at,
          navigateTo: `/my-account-page?userId=${profile?.id}`
        });
      });
    }

    // Fetch course completions (if actionType allows)
    if (actionType === 'all' || actionType === 'course_completion') {
      const { data: completions, error: completionError } = await supabase?.from('user_lesson_progress')?.select(`
          id,
          completed_at,
          lesson_section,
          quiz_score,
          is_quiz_perfect,
          user_id,
          song_id,
          user_profiles!user_lesson_progress_user_id_fkey (
            id,
            full_name,
            username,
            profile_photo_url,
            role
          ),
          songs (
            title,
            musical_id,
            musicals (
              title
            )
          )
        `)?.eq('lesson_status', 'completed')?.not('completed_at', 'is', null)?.gte('completed_at', cutoffTimestamp)?.order('completed_at', { ascending: false })?.limit(limit);

      if (completionError) throw completionError;

      completions?.forEach(completion => {
        if (!completion?.user_profiles) return;
        
        // Apply user type filter
        if (userType !== 'all' && completion?.user_profiles?.role !== userType) return;

        const achievementText = completion?.is_quiz_perfect 
          ? 'with perfect quiz score!' 
          : completion?.quiz_score > 0 
            ? `(quiz score: ${completion?.quiz_score}%)` 
            : '';

        activities?.push({
          id: `completion_${completion?.id}`,
          type: 'course_completion',
          action: 'Lesson Completed',
          description: `completed ${completion?.lesson_section} for "${completion?.songs?.title}" ${achievementText}`,
          user: {
            id: completion?.user_profiles?.id,
            name: completion?.user_profiles?.full_name || completion?.user_profiles?.username,
            username: completion?.user_profiles?.username,
            avatar: completion?.user_profiles?.profile_photo_url,
            role: completion?.user_profiles?.role
          },
          metadata: {
            songTitle: completion?.songs?.title,
            songId: completion?.song_id,
            musicalTitle: completion?.songs?.musicals?.title,
            lessonSection: completion?.lesson_section,
            quizScore: completion?.quiz_score,
            isPerfect: completion?.is_quiz_perfect
          },
          timestamp: completion?.completed_at,
          navigateTo: `/song-detail-page?id=${completion?.song_id}`
        });
      });
    }

    // Sort all activities by timestamp (most recent first)
    activities?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    // Return limited results
    return activities?.slice(0, limit);

  } catch (error) {
    console.error('Error fetching recent activities:', error);
    throw error;
  }
};

/**
 * Fetch activity analytics for dashboard overview
 * @param {number} timeRange - Time range in hours
 * @returns {Promise<Object>} Activity statistics
 */
export const fetchActivityAnalytics = async (timeRange = 168) => {
  try {
    const cutoffTime = new Date();
    cutoffTime?.setHours(cutoffTime?.getHours() - timeRange);
    const cutoffTimestamp = cutoffTime?.toISOString();

    // Count song additions
    const { count: songCount, error: songError } = await supabase?.from('songs')?.select('id', { count: 'exact', head: true })?.gte('created_at', cutoffTimestamp)?.not('created_by', 'is', null);

    if (songError) throw songError;

    // Count profile updates
    const { count: profileCount, error: profileError } = await supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true })?.gte('updated_at', cutoffTimestamp)?.neq('created_at', supabase?.raw('updated_at'));

    if (profileError) throw profileError;

    // Count course completions
    const { count: completionCount, error: completionError } = await supabase?.from('user_lesson_progress')?.select('id', { count: 'exact', head: true })?.eq('lesson_status', 'completed')?.gte('completed_at', cutoffTimestamp);

    if (completionError) throw completionError;

    // Count active users (users with any activity)
    const { data: activeUsers, error: activeError } = await supabase?.from('user_profiles')?.select('id')?.gte('last_activity_date', cutoffTimestamp?.split('T')?.[0]);

    if (activeError) throw activeError;

    return {
      totalActivities: (songCount || 0) + (profileCount || 0) + (completionCount || 0),
      songAdditions: songCount || 0,
      profileUpdates: profileCount || 0,
      courseCompletions: completionCount || 0,
      activeUsers: activeUsers?.length || 0,
      timeRange
    };

  } catch (error) {
    console.error('Error fetching activity analytics:', error);
    throw error;
  }
};

/**
 * Fetch engagement trends over time
 * @param {number} days - Number of days to analyze
 * @returns {Promise<Array>} Daily engagement data
 */
export const fetchEngagementTrends = async (days = 7) => {
  try {
    const trends = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date?.setDate(date?.getDate() - i);
      const dateStr = date?.toISOString()?.split('T')?.[0];
      
      const nextDate = new Date(date);
      nextDate?.setDate(nextDate?.getDate() + 1);
      const nextDateStr = nextDate?.toISOString()?.split('T')?.[0];

      // Count activities for this day
      const { count: songCount } = await supabase?.from('songs')?.select('id', { count: 'exact', head: true })?.gte('created_at', dateStr)?.lt('created_at', nextDateStr)?.not('created_by', 'is', null);

      const { count: completionCount } = await supabase?.from('user_lesson_progress')?.select('id', { count: 'exact', head: true })?.eq('lesson_status', 'completed')?.gte('completed_at', dateStr)?.lt('completed_at', nextDateStr);

      const { data: xpEvents } = await supabase?.from('user_xp_events')?.select('amount')?.gte('created_at', dateStr)?.lt('created_at', nextDateStr);

      const totalXp = xpEvents?.reduce((sum, event) => sum + event?.amount, 0) || 0;

      trends?.push({
        date: dateStr,
        songAdditions: songCount || 0,
        courseCompletions: completionCount || 0,
        totalXpEarned: totalXp,
        totalActivities: (songCount || 0) + (completionCount || 0)
      });
    }

    return trends;

  } catch (error) {
    console.error('Error fetching engagement trends:', error);
    throw error;
  }
};

/**
 * Export activity data for reporting
 * @param {Object} filters - Export filters
 * @returns {Promise<Blob>} CSV file blob
 */
export const exportActivityData = async (filters) => {
  try {
    const activities = await fetchRecentActivities({ ...filters, limit: 1000 });

    // Create CSV content
    const headers = ['Timestamp', 'Action Type', 'User', 'User Role', 'Description', 'Details'];
    const rows = activities?.map(activity => [
      new Date(activity.timestamp)?.toLocaleString(),
      activity?.action,
      activity?.user?.name,
      activity?.user?.role,
      activity?.description,
      JSON.stringify(activity?.metadata)
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    return new Blob([csvContent], { type: 'text/csv' });

  } catch (error) {
    console.error('Error exporting activity data:', error);
    throw error;
  }
};